<template>
  <div class="contain callpop-history">
    <div class="callpop-history__head">
      <h3 class="callpop-history__title">{{ text.historyTitle }}</h3>
    </div>

    <el-form class="callpop-history__filter" size="mini" inline>
      <el-form-item :label="text.historyTargetTpLabel">
        <el-select v-model="targetTp" class="callpop-history__tp" clearable @change="onTargetTp">
          <el-option v-for="row in targetTypes" :key="row.value" :label="row.label" :value="row.value"></el-option>
        </el-select>
      </el-form-item>

      <!-- 대상 지목 — 운영사·관리점 계정에게는 **필수**다(§3-5: 지목이 없으면 400) -->
      <el-form-item v-if="targetTp === HISTORY_TARGET.OPERATOR" :label="text.historyTargetLabel">
        <el-select
          v-model="targetCd"
          class="callpop-history__target"
          clearable
          filterable
          :disabled="!!operatorsDenied"
          :placeholder="operatorsDenied || text.operatorLabel">
          <el-option
            v-for="row in operatorOptions"
            :key="row.id"
            :label="row.label"
            :value="row.operatorCd"></el-option>
        </el-select>
      </el-form-item>
      <el-form-item v-else-if="targetTp === HISTORY_TARGET.AGENCY" :label="text.historyTargetLabel">
        <!-- 이력은 지난 조직도 대상이다 — 검색(활성만) 대신 목록 API(비활성 포함)가 후보를 낸다(R-A) -->
        <AgencyPicker
          v-model="agencyId"
          class="callpop-history__target"
          :source="agencyListRead"
          :placeholder="text.agencyLabel"
          @change="onAgency" />
      </el-form-item>
      <el-form-item :label="text.historyPeriodLabel">
        <el-date-picker
          v-model="period"
          class="callpop-history__period"
          type="daterange"
          value-format="yyyy-MM-dd"
          range-separator="~"
          unlink-panels />
      </el-form-item>
      <el-form-item>
        <el-button size="mini" icon="el-icon-search" @click="searchFirstPage">{{ text.searchAction }}</el-button>
      </el-form-item>
    </el-form>

    <el-alert v-if="denied" :title="denied" type="info" :closable="false" show-icon />
    <!-- 서버가 「그 대상은 못 본다」고 답했다 — 빈 표는 「이력이 없다」는 **다른 말**이다 -->
    <el-alert v-else-if="readDenied" :title="readDenied" type="info" :closable="false" show-icon />
    <!-- 전체 목록을 못 보는 계정에게 **먼저** 말한다 — 부르면 400 이고, 그 400 은 답이 아니라 헛걸음이다 -->
    <el-alert v-else-if="targetRequired" :title="text.historyTargetRequired" type="info" :closable="false" show-icon />

    <template v-else>
      <OrgHistoryTable :items="items" :refs="refs" :loading="loading" />
      <list-pagination
        :search-form="searchForm"
        :total="totalCount"
        :page-sizes="pageSizes"
        @page-change="onPageChange"
        @size-change="onSizeChange" />
    </template>
  </div>
</template>

<script>
import AgencyPicker from '@/components/callpop/AgencyPicker.vue'
import OrgHistoryTable from '@/components/callpop/OrgHistoryTable.vue'
import { canRead, permittedRead } from '@/plugins/http/permittedRead'
import { httpErrorMessage, httpStatusOf } from '@/plugins/http/policy'
import { ROLE_TEXT } from '@/utils/adminRole'
import {
  AGENCY_LIST_READ,
  CALLPOP_ACTION,
  CALLPOP_API,
  CALLPOP_PAGE_SIZE,
  HISTORY_TARGET,
  STORE_PAGE_SIZES,
  cleanParams,
  historyTargetText,
  itemsOf,
  pageEnvelope,
  periodParams,
  refsOf,
} from '@/utils/callpopOrg'
import { CALLPOP_TEXT } from '@/utils/callpopText'
import { latestGuard } from '@/utils/latestGuard'
import { toNumberOrNull } from '@/utils/numberValue'
import callpopOperatorsMixin from '@/mixins/callpopOperatorsMixin'
import mainVueMixin from '@/mixins/mainVueMixin'

/**
 * 이력 조회 — **400 의 토스트를 끈다**(§16.175 옵트아웃). 이 화면에서 「대상을 지목하라」는 400 은
 * 오류가 아니라 **답**이고, 대체 동작은 그 사실을 인라인 안내로 말하며 대상 칸을 요구하는 것이다.
 */
const HISTORY_READ = {
  action: CALLPOP_ACTION.ORG_HISTORY_GET,
  url: `${CALLPOP_API}/history`,
  showErrorMessage: false,
}

/**
 * **변경 이력** (화면 #4, `/v/callpop/history`).
 *
 * 서버는 이력 행에 운영사 축을 갖고 있지 않아 행 단위로 범위를 좁힐 수 없다(서버 보고서 §8 10). 그래서
 * **전체 목록은 전부를 볼 수 있는 계정만** 부를 수 있고, 운영사·관리점은 `targetTp`·`targetCd` 로 대상을
 * 지목해야 한다 — 지목 없이 부르면 400 이다.
 *
 * 「이 계정이 전체를 볼 수 있는가」는 화면이 역할로 판정하지 않는다: **한 번 물어보고 400 이 오면**
 * 그때부터 지목을 요구한다([targetRequired]). 서버가 정본이고 화면은 그 답을 기억할 뿐이다.
 */
export default {
  name: 'CallpopHistoryView',

  mixins: [mainVueMixin, callpopOperatorsMixin],

  components: { AgencyPicker, OrgHistoryTable },

  data() {
    return {
      text: CALLPOP_TEXT,
      HISTORY_TARGET,
      agencyListRead: AGENCY_LIST_READ,
      pageSizes: STORE_PAGE_SIZES.slice(),
      targetTp: '',
      targetCd: null,
      agencyId: '',
      period: null,
      items: [],
      /** 같은 응답의 이름 사전 — PK 를 이름으로 푸는 것은 서버가 한 번에 한다(§3-5) */
      refs: null,
      totalCount: 0,
      loading: false,
      /**
       * 서버가 「대상을 지목하라」고 답했는가(400) — 역할로 미리 정하지 않는다.
       *
       * **한 번 참이 되면 내려가지 않는다.** 그것은 이 계정의 성질(전체 목록을 볼 수 없다)이지 이번
       * 질의의 사정이 아니다 — 세션 동안 변하지 않는다. 대상을 지정해 한 번 성공했다고 지우면, 그 뒤
       * 대상을 비울 때마다 **같은 400 을 다시 받으러 간다**(R3 실행으로 확인).
       */
      needsTarget: false,
      /** 서버가 403 으로 답한 이유 — 빈 표로 접지 않는다 */
      readDenied: '',
      /** 늦게 온 응답이 최신 조건의 결과를 덮지 못하게(`utils/latestGuard`) */
      guard: latestGuard(),
      searchForm: { currPage: 0, pageSize: CALLPOP_PAGE_SIZE },
    }
  },

  computed: {
    denied() {
      return canRead(HISTORY_READ) ? '' : ROLE_TEXT.scopeReadDenied
    },
    /**
     * 고를 수 있는 대상 종류 — **매장 매핑은 없다**.
     *
     * 그 축의 대상은 `store_cd`(내부 PK)인데 사람이 그것을 얻을 화면이 없다. 자유 입력을 두면 매장 ID
     * (`S260001287`)나 매장명을 넣게 되고, 그것은 이 축에서 **영원히 답이 될 수 없는 값**이다. 매장 한
     * 곳의 이력으로 가는 길은 이미 있다 — 매장 입점 현황 행의 「이력」이 같은 표를 연다.
     */
    targetTypes() {
      return [HISTORY_TARGET.OPERATOR, HISTORY_TARGET.AGENCY].map((value) => ({
        value,
        label: historyTargetText(value),
      }))
    },
    /**
     * 지금 지목된 대상 — **숫자로 읽히지 않으면 `null`** 이다([toNumberOrNull]).
     *
     * 두 축 모두 선택기가 주는 PK 라 숫자가 아닐 일이 없지만, 관문은 그대로 둔다: 숫자 축에 `NaN` 이
     * 실리면 그 값은 **있다와 없다 양쪽으로 행세한다**(`NaN` 은 질의에 실리면서 `!NaN` 은 참이다).
     */
    target() {
      const raw = this.targetTp === HISTORY_TARGET.OPERATOR ? this.targetCd : this.agencyId

      return toNumberOrNull(raw)
    },
    targetRequired() {
      return this.needsTarget && this.target == null
    },
  },

  created() {
    this.loadOperators()
    this.search()
  },

  beforeDestroy() {
    this.guard.close()
  },

  methods: {
    /**
     * 대상 종류를 바꾼다 — 고르던 대상은 버린다.
     *
     * **`needsTarget` 은 내리지 않는다.** 서버는 `targetCd` 하나로 400 을 내므로(`requireHistoryScope`)
     * 종류만 고른 상태는 여전히 「대상을 지목하지 않은」 질의다. 여기서 내리면 그 질의가 나가 400 을 받고,
     * 배너가 사라진 자리에 빨간 토스트가 뜨며 검색마다 실패가 되풀이된다. 내려가는 자리는 **조회 성공** 하나다.
     */
    onTargetTp() {
      this.targetCd = null
      this.agencyId = ''
      this.searchFirstPage()
    },

    onAgency() {
      this.searchFirstPage()
    },

    searchFirstPage() {
      this.searchForm.currPage = 0
      this.search()
    },

    onPageChange(page) {
      this.searchForm.currPage = page - 1
      this.search()
    },

    onSizeChange(size) {
      this.searchForm.pageSize = size
      this.searchForm.currPage = 0
      this.search()
    },

    async search() {
      if (this.denied || this.targetRequired) return

      const seq = this.guard.begin()
      this.loading = true

      // `params` 를 try 밖에 두는 이유: 400 의 뜻을 **실제로 보낸 값**으로 판정한다(아래 catch)
      const range = periodParams(this.period)
      const params = cleanParams({
        targetTp: this.targetTp,
        targetCd: this.target,
        from: range.from,
        to: range.to,
        page: this.searchForm.currPage,
        size: this.searchForm.pageSize,
      })

      try {
        const outcome = await permittedRead(Object.assign({}, HISTORY_READ, { params }))
        if (!this.guard.isLatest(seq)) return

        // 403 은 「그 대상은 못 본다」는 답이다 — 빈 표로 접으면 「이력이 없다」라는 다른 말이 된다
        if (!outcome.allowed) {
          this.readDenied = outcome.message || ROLE_TEXT.scopeReadDenied
          this.items = []
          this.refs = null
          this.totalCount = 0

          return
        }

        const envelope = pageEnvelope(outcome.data)

        this.readDenied = ''
        this.items = envelope.items
        this.refs = refsOf(outcome.data)
        this.totalCount = envelope.totalCount
        // `GET /callpop/history` 는 `{items}` 만 줄 수도 있다 — 그때는 받은 만큼이 전부다
        if (!envelope.totalCount && envelope.items.length > 0) this.totalCount = itemsOf(outcome.data).length
      } catch (error) {
        if (!this.guard.isLatest(seq)) return

        this.items = []
        this.refs = null
        this.totalCount = 0
        // 「대상을 지목하라」는 답이다 — 판정의 기준은 **보낸 요청에 `targetCd` 가 없었는가** 하나다.
        //
        // 서버가 그렇게 판정하기 때문이다(`requireHistoryScope`: `targetCd == null` 이면 400, `targetTp`
        // 가 있어도 마찬가지). 여기에 `targetTp` 조건을 더하면 **종류만 고른 질의의 400** 이 다른 실패로
        // 읽혀 배너가 사라지고 빨간 토스트가 뜬다. 읽을 수 없는 입력은 [targetUnreadable] 이 전송 전에 막는다
        if (httpStatusOf(error) === 400 && params.targetCd == null) {
          this.needsTarget = true
        } else {
          // 토스트를 껐으므로(`HISTORY_READ`) 그 밖의 실패는 여기서 말한다 — 침묵으로 두지 않는다
          this.$message({ type: 'error', message: httpErrorMessage(error, this.text.loadFailed) })
        }
      } finally {
        if (this.guard.isLatest(seq)) this.loading = false
      }
    },
  },
}
</script>

<style scoped>
.callpop-history__head {
  margin-bottom: 12px;
}
.callpop-history__title {
  font-size: 16px;
  margin: 0;
}
.callpop-history__tp {
  width: 130px;
}
.callpop-history__target {
  width: 200px;
}
.callpop-history__period {
  width: 250px;
}
</style>
